import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Icon from '../../../../../../core/shared/Icon'
import {RadioCircle} from '../../../../../../accountManagement/shared/RadioCircle'
import {
  selectLocations,
  selectSelectedLocationId,
  setSelectedLocationId,
} from '../../../../../../app-store/PurchaseLinkSlice'
import {
  Wrapper,
  LabelBlock,
  PinIconSlot,
  LabelText,
  TriggerButton,
  Chip,
  ChipLabel,
  ChevronSlot,
  OptionsPanel,
  OptionRow,
  OptionLabel,
} from './ParentPayLocationSelector.styles'

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// The account-header "Create Purchase Link" flow (see AccountRow's
// hasParentPayChild / onOrderForParent) only ever shows one location's
// catalog at a time, same as LocationSelector — but its option list is
// pre-filtered to just that account's parent-pay children, and rendered as
// a checkbox list that behaves like a radio group: checking a location
// switches selectedLocationId and unchecks whichever was active, never more
// than one at once, and is rendered with radio-button styling to match.
// The Order Summary lists every location from this same set independently
// regardless of which one is currently active here.
export function ParentPayLocationSelector() {
  const dispatch = useDispatch()
  const locations = useSelector(selectLocations)
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  if (locations.length === 0) return null

  const activeLocation =
    locations.find(l => l.locationId === selectedLocationId) ?? locations[0]

  return (
    <Wrapper ref={wrapperRef}>
      <LabelBlock>
        <PinIconSlot aria-hidden="true">
          <Icon name="locationSelector" size="md" />
        </PinIconSlot>
        <LabelText>
          <span>
            <b>Select</b>
          </span>
          <span>Location</span>
        </LabelText>
      </LabelBlock>

      <TriggerButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(o => !o)}
      >
        <Chip>
          <ChipLabel>
            {activeLocation.locationName || activeLocation.thryvId}
          </ChipLabel>
        </Chip>
        <ChevronSlot open={isOpen}>
          <ChevronDownIcon />
        </ChevronSlot>
      </TriggerButton>

      {isOpen && (
        <OptionsPanel role="radiogroup">
          {locations.map(loc => {
            const isChecked = loc.locationId === selectedLocationId
            return (
              <OptionRow
                key={loc.locationId}
                type="button"
                role="radio"
                aria-checked={isChecked}
                onClick={() => {
                  dispatch(setSelectedLocationId(loc.locationId))
                  setIsOpen(false)
                }}
              >
                <RadioCircle
                  active={isChecked}
                  variant="check"
                  aria-hidden="true"
                />
                <OptionLabel>{loc.locationName || loc.thryvId}</OptionLabel>
              </OptionRow>
            )
          })}
        </OptionsPanel>
      )}
    </Wrapper>
  )
}
